/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import debounce from 'lodash.debounce';
import isEmpty from 'lodash.isempty';
import isEqual from 'lodash.isequal';
import uniqBy from 'lodash.uniqby';
import {useRouter} from 'next/compat/router';
import {clearQueryParams} from '@ion/applications/src/query/state';
import {translate} from '@ion/core/src/locale/locale';
import {isSCIDevice} from '@ion/core/src/services/ArcGISUtils';
import {GenericObject} from '@ion/core/src/types/entities';
import {isArrayDeepEmpty} from '@ion/core/src/utils/ArrayUtil';
import {
  dateToMoment,
  endOfDay,
  startOfDay
} from '@ion/core/src/utils/DateUtil';
import {isObjectEmpty} from '@ion/core/src/utils/ObjectUtil';
import {MobileMenuButton} from '@ion/dls/src/components/buttons/MobileMenuButton';
import {PrimaryButton} from '@ion/dls/src/components/buttons/PrimaryButton';
import {AutocompleteSearch} from '@ion/dls/src/components/search/AutocompleteSearch';
import {hasSelectionInFilters} from '@ion/dls/src/filters/FilterHelpers';
import MoreFilters from '@ion/dls/src/filters/MoreFilters';
import SaveFilter from '@ion/dls/src/filters/SaveFilter';
import {getSelectedCounts} from '@ion/dls/src/filters/TreeFilterUtils';
import {FilterState, useFilterState} from '@ion/dls/src/filters/useFilterState';
import {FilterParamsProps, useFilterSubmit} from '@ion/dls/src/filters/useFilterSubmit';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {Hidden} from '@ion/dls/src/layout/Hidden';
import {dlsColor, dlsZIndex} from '@ion/dls/src/theme/theme';
import {
  DateSelectorValues,
  Filters,
  LastFilteredDateProps,
  SCFilterProps,
  TrackingMode
} from '@ion/dls/src/types/types';
import {Route} from '@/routes/RouteDefinitions';
import {useReturnToLiveTracking} from '@/routes/tracking/hooks/history/useReturnToLiveTracking';
import useBoundaryFilter from '@/routes/tracking/hooks/tracking/useBoundaryFilter';
import Features, {hasHistoryAccess} from '@/security/Features';
import {
  RESET_FILTER_TREE_NODES,
  setFilterTreeNodes,
  updateTreeKeys
} from '@/store/actions/groups-filter';
import {setSideSheetStatus} from '@/store/actions/nav';
import {
  applyAssetHistoryDateFilter,
  setIsMobileMapView
} from '@/store/actions/reports';
import {
  applySaveFilter,
  clearSelectedTrackingSearchResult,
  resetTrackingAssetsState,
  search,
  setAssetStatusFilters,
  setAssetTypeFilters,
  setCategoryFilters,
  setGeofenceFilters,
  setGeoFencePanelOpen,
  setGroupFilters,
  setRouteFilters,
  setSelectedFiltersActive,
  setSelectedTrackingSearchResult,
  setTrackingMode
} from '@/store/actions/tracking';
import {filterPersistence, getSubAccount, isCurrentPage} from '@/store/helpers';
import {
  accountsSelector,
  globalCurrentGroupSelector,
  globalGroupIdSelector,
  globalGroupSelector
} from '@/store/selectors/global-group-chooser-selectors';
import {
  assetLoadingSelector,
  assetsStoredSelector,
  getTreeNodeKeysSelector,
  includeSCDeviceSelector,
  scDeviceTypesSelector,
  treeKeysTrackingSelector,
  treeNodesSelector
} from '@/store/selectors/groups-filter-selector';
import {ignoreQuerySelector} from '@/store/selectors/nav-selectors';
import {dateValueSelector} from '@/store/selectors/reports-selectors';
import {
  appliedSavedFiltersSelector,
  findNearbyIsActiveSelector,
  legacySearchHistorySelector,
  queryParamsDateSelector,
  queryParamsSelector,
  savingGeofenceSelector,
  searchLoadingSelector,
  searchResultsSelector,
  searchSelectedSelector,
  trackingFiltersSelector,
  trackingModeSelector
} from '@/store/selectors/tracking-selectors';
import {useAssetListState} from '../../hooks/useAssetListState';
import {useLoadGroupAssets} from '../../hooks/useLoadGroupAssets';
import {useSCDevices} from '../../hooks/useSCDevices';
import {useUpdateItemStored} from '../../hooks/useUpdateItemStored';
import {useFleetResetUpdates} from '../../routes/tracking2/hooks/useFleetResetUpdates';
import {BoundaryFilter} from './implementations/BoundaryFilter';
import {CategoriesFilter} from './implementations/CategoriesFilter';
import {DateFilter, DateFilterParamsType} from './implementations/DateFilter';
import {GeofencesFilter} from './implementations/GeofencesFilter';
import {GroupsFilter, GroupsFilterParamsType} from './implementations/GroupsFilter';
import {RoutesFilter} from './implementations/RoutesFilter';
import {StatusFilter} from './implementations/StatusFilter';
import {TypesFilter} from './implementations/TypesFilter';
import {
  dateFilterMapping,
  formatFilterState,
  getGroupsAssetsFilterUpdates,
  getTrackingSearchResults,
  isFromGroupsAssetsFilter
} from './helpers';
import {shouldRenderGroupsFilter} from './TrackingFilterUtils';
import {FilterParamsType} from './types';
import {useAncestorIds} from './useAncestorIds';
import {useGeofenceDescendantIds} from './useGeofenceDescendantIds';

/**
 * Defines the prop definitions for TrackingFilters ...
 *
 * closeMobileMenu:       If true, mobile filters menu is closed else not.
 * trackingSearchOpen:    Check if tracking search is open.
 * setTrackingSearchOpen: Function to toggle tracking search menu.
 */
export interface TrackingFilterProps {
  closeMobileMenu?: Function;
  trackingSearchOpen?: boolean;
  setTrackingSearchOpen?: Function;
  resetSideSheet?: Function;
};

export interface UseStylesProps {
  isPhone?: boolean;
  filterWidth?: number;
};

type FilterSave = {
  state?: FilterState;
  filter?: FilterState;
  filterKey?: string;
  isRemove?: boolean;
};

// The purpose of these constants is to apply percentage-based reductions to the filters and an input field, 
// allowing the filters to be displayed together on large screens without being pushed into the ‘More Filters’ option.
const filterWidthRatio = 80/100;
const filterWrapperWidthPercentage = 85;
const autoSearchWrapperWidthPercentage = 100 - filterWrapperWidthPercentage;
const ratioFiltersMinWidthThreshold = 1400;
const screenThreshold = 1280;

const toggleMobileStyle = (manual, auto) => ({isPhone}: UseStylesProps) => isPhone ? manual : auto;

const filterWrapperWidthCallback = ({isPhone}: UseStylesProps) => {
  if (isPhone) {
    return '100%';
  };

  if (window.innerWidth > screenThreshold) {
    return `${filterWrapperWidthPercentage}%`;
  };

  return '80%';
};

const autoSearchWrapperWidthCallback = () => {
  if (window.innerWidth > screenThreshold) {
    return `${autoSearchWrapperWidthPercentage}%`;
  };

  return '20%';
};

// defines the styles used by TrackingFilters ...
export const searchTransition = '250ms ease-in-out';
const useStyles = makeStyles(theme => ({
  filterWrapper : {
    width                   : filterWrapperWidthCallback,
    display                 : 'flex',
    maxWidth                : '100%',
    transition              : `max-width ${searchTransition}, width ${searchTransition}`,
    alignItems              : 'center',
    justifyContent          : 'flex-start',
    '& #moreFiltersWrapper' : {
      padding : 0,
      margin  : '4px',
      border  : `1px solid ${dlsColor('grey5')}`
    }
  },
  fullWidthSearch : {
    '&&' : {
      width      : '100%',
      maxWidth   : '100%',
      transition : `max-width ${searchTransition}, width ${searchTransition}`
    }
  },
  hideFilters : {
    display : 'none',
    '&&'    : {
      width      : '0px',
      maxWidth   : '0px',
      transition : `max-width ${searchTransition}, width ${searchTransition}`,
      '& svg'    : {
        width : 0
      }
    }
  },
  mobileTrackingFiltersWrapper : {
    display                        : 'flex',
    alignItems                     : 'center',
    marginTop                      : '10px',
    overflow                       : 'hidden',
    flexWrap                       : 'wrap',
    padding                        : '0px 12px',
    zIndex                         : dlsZIndex('trackingFilterWrapper'),
    [theme.breakpoints.down('sm')] : {
      width           : '-webkit-fill-available',
      padding         : '12px',
      backgroundColor : dlsColor('white')
    }
  },
  desktopTrackingFiltersWrapper : {
    display : 'contents !important'
  },
  filter : {
    width : ({isPhone, filterWidth}: UseStylesProps) => {
      return isPhone ? '95%' : `${filterWidth}px`;
    },
    margin                                 : toggleMobileStyle('0 auto', '0 3px'),
    border                                 : toggleMobileStyle('none', `1px solid ${dlsColor('grey5')}`),
    backgroundColor                        : 'transparent !important',
    '& .MuiSelect-select.MuiSelect-select' : {
      minWidth    : 'initial !important',
      paddingLeft : '0 !important'
    },
    '& [data-id=megamenu]' : {
      display            : 'contents',
      '& .MuiInput-root' : {
        cursor                                 : 'pointer',
        height                                 : '49px',
        display                                : 'flex',
        padding                                : '10px !important',
        alignItems                             : 'center',
        justifyContent                         : 'space-between',
        maxWidth                               : 'initial !important',
        backgroundColor                        : dlsColor('white'),
        '& .MuiSelect-root [data-title=title]' : {
          overflow     : 'hidden',
          maxWidth     : '170px',
          whiteSpace   : 'pre',
          textOverflow : 'ellipsis',
          '& span'     : {
            display : 'inline'
          }
        }
      }
    }
  },
  header : {
    display                : 'flex',
    justifyContent         : 'space-between',
    alignItems             : 'center',
    width                  : '100%',
    padding                : '0 4px',
    borderBottom           : `1px solid ${dlsColor('grey5')}`,
    '& > span:first-child' : {
      cursor     : 'pointer',
      lineHeight : '10px',
      marginLeft : '-10px'
    }
  },
  title : {
    letterSpacing : '1px',
    textTransform : 'capitalize'
  },
  autoSearchWrapper : {
    width              : autoSearchWrapperWidthCallback,
    margin             : '0 3px',
    border             : `1px solid ${dlsColor('grey5')}`,
    transition         : `max-width ${searchTransition}, width ${searchTransition}`,
    backgroundColor    : 'transparent !important',
    '& .MuiInput-root' : {
      height  : '49px',
      padding : '10px !important'
    }
  },
  autocompletePopper : {
    width : 'calc(100% - 8px) !important'
  },
  closeFilters : {
    width     : '96%',
    marginTop : '24px'
  },
  mobileSave : {
    marginTop : '16px'
  },
  saveFilter : {
    display : 'block'
  },
  mobileClear : {
    color         : dlsColor('aaBlue'),
    fontFamily    : 'roboto',
    textTransform : 'uppercase',
    fontSize      : '14px',
    fontWeight    : 600
  }
}));

// defines the list of filters to display on tracking map
export const FiltersList = [
  Filters.groups,
  Filters.assetTypes,
  Filters.assetCategories,
  Filters.date,
  Filters.geoFences,
  Filters.routes,
  Filters.assetStatus,
  Filters.boundary
];

export const TRACKING_FILTER_PREFERENCES = 'tracking:saved-filters';

export const FilterActions = {
  [Filters.groups]          : setGroupFilters,
  [Filters.assets]          : setGroupFilters,
  [Filters.devices]         : setGroupFilters,
  [Filters.assetTypes]      : setAssetTypeFilters,
  [Filters.assetCategories] : setCategoryFilters,
  [Filters.geoFences]       : setGeofenceFilters,
  [Filters.routes]          : setRouteFilters,
  [Filters.assetStatus]     : setAssetStatusFilters
};

function handleResize(filterList) {
  const FILTERS_COUNT        = filterList?.length;
  const FILTER_WRAPPER_WIDTH = document.getElementById('filterWrapper')?.offsetWidth;
  const FILTER_WIDTH         = FILTER_WRAPPER_WIDTH > ratioFiltersMinWidthThreshold ? (200 * filterWidthRatio) : 200;
  const SAVE_FILTER_WIDTH    = document.getElementById('saveFilter')?.offsetWidth;
  const FILTERS_WIDTH        = FILTER_WRAPPER_WIDTH - SAVE_FILTER_WIDTH;

  const RAW_COUNT         = Math.min(FILTERS_COUNT, Math.floor(FILTERS_WIDTH / FILTER_WIDTH));
  const MAX_FILTERS_COUNT = RAW_COUNT > 0 ? RAW_COUNT : 1;

  const remainingWidth = FILTERS_WIDTH - (RAW_COUNT * FILTER_WIDTH);

  // Base per-filter width plus evenly distributed remainder
  let WIDTH = remainingWidth > 0
    ? FILTER_WIDTH + (remainingWidth / MAX_FILTERS_COUNT)
    : FILTER_WIDTH;

  // If all filters fit, slightly increase width
  if (FILTERS_COUNT === MAX_FILTERS_COUNT) {
    WIDTH += (WIDTH / FILTERS_COUNT);
  }

  // This flag displays the single filter button when only one ‘More filters’ item remains.
  const isOnlyOneItemLeft = (FILTERS_COUNT - MAX_FILTERS_COUNT) === 1;

  return {
    width : WIDTH,
    count : isOnlyOneItemLeft ? (MAX_FILTERS_COUNT + 1) : MAX_FILTERS_COUNT
  };
};

export const getUniqueFiltersCount = (selectedFilters: {id: number, name: string}[]): number => {
  const selectedUniqueIds = Array.from(new Set(selectedFilters?.map(({id}) => id)));
  return selectedUniqueIds?.length;
};

const normalizePreference = (preference: any, accountHierarchy: any) => {
  let newPreference = {};
  Object.keys(preference).forEach(key => {
    newPreference = {
      ...newPreference,
      [key] : {
        ...preference[key],
        ...formatFilterState(preference[key], accountHierarchy)
      }
    };
  });
  return newPreference;
};

/**
 * The TrackingFilters component manages rendering the individual filters
 * within tracking page ...
 *
 * ```typescript
 *
 * import TrackingFilters from '@/src/components/filter/TrackingFilters';
 *
 * <TrackingFilters closeMobileMenu={closeMobileMenu} />
 *
 * ```
 */
export const TrackingFilters = forwardRef(({
  closeMobileMenu,
  trackingSearchOpen,
  setTrackingSearchOpen,
  resetSideSheet
}: TrackingFilterProps, ref) => {
  const route                                   = useRouter();
  const findNearbyIsActive                      = useSelector(findNearbyIsActiveSelector);
  const trackingMode                            = useSelector(trackingModeSelector);
  const [filterList, setFilterList]             = useState(FiltersList);
  const {width, count}                          = handleResize(filterList);
  const {isPhone}                               = useMedia();
  const [filterWidth, setFilterWidth]           = useState(width);
  const classes                                 = useStyles({isPhone, filterWidth});
  const trackingFilters                         = useSelector(trackingFiltersSelector);
  const [filterState, setFilterState]           = useFilterState(trackingFilters);
  const globalGroupId                           = useSelector(globalGroupIdSelector);
  const accountHierarchy                        = useSelector(accountsSelector);
  const globalGroup                             = useSelector(globalGroupSelector);
  const descendantAccountId                     = useGeofenceDescendantIds();
  const dispatch                                = useDispatch();
  const [moreFilters, setMoreFilters]           = useState([]);
  const moreFiltersRef                          = useRef<GenericObject>({});
  const preferences                             = filterPersistence(TRACKING_FILTER_PREFERENCES) || {};
  const [savedPreferences, setSavedPreferences] = useState(normalizePreference(preferences, accountHierarchy));
  const [filterId, setFilterId]                 = useState<Filters>();
  const [filterParams, setFilterParams]         = useState<FilterParamsProps>({});
  const [maxFilterCount, setMaxFilterCount]     = useState(count);
  const [showMoreFilters, setShowMoreFilters]   = useState(false);
  const searchResult                            = useSelector(searchSelectedSelector);
  const treeNodes                               = useSelector(treeNodesSelector);
  const treeKeys                                = useSelector(treeKeysTrackingSelector);
  const nodeKeys                                = useSelector(getTreeNodeKeysSelector);
  const assetLoading                            = useSelector(assetLoadingSelector);
  const cachedData                              = useSelector(assetsStoredSelector);
  const includeSCDevice                         = useSelector(includeSCDeviceSelector);
  const currentGroup                            = useSelector(globalCurrentGroupSelector);
  const SCDeviceTypes                           = useSelector(scDeviceTypesSelector);
  const savingGeofence                          = useSelector(savingGeofenceSelector);
  const [view, setView]                         = useState({
    [Filters.date]            : false,
    [Filters.groups]          : false,
    [Filters.assetTypes]      : false,
    [Filters.assetCategories] : false,
    [Filters.geoFences]       : false,
    [Filters.routes]          : false,
    [Filters.assetStatus]     : false,
    [Filters.boundary]        : false
  });
  const trackingSearchResults   = useSelector(searchResultsSelector);
  const trackingSearchLoading   = useSelector(searchLoadingSelector);
  const trackingSearchHistory   = useSelector(legacySearchHistorySelector);
  const appliedSaveFilter       = useSelector(appliedSavedFiltersSelector);
  const sortedTrackingResults   = getTrackingSearchResults(trackingSearchResults);
  const trackingSearchOptions   = sortedTrackingResults ?? [];
  const moreOptionKeys          = moreFilters?.map((item) => Object.keys(item)?.[0]);
  const moreOptionValues        = moreFilters?.map((item) => Object.values(item)?.[0]);
  const moreOptionsCount        = moreOptionKeys?.filter(key => filterState?.[key]?.selected?.length > 0)?.length;
  const ancestorAccountIds      = useAncestorIds();
  const onLoadGroupAssets       = useLoadGroupAssets();
  const returnToLiveTracking    = useReturnToLiveTracking();
  const onCheckIncludeSCDevices = useSCDevices();
  const onUpdateItemStored      = useUpdateItemStored();
  const showAssetsListRef       = useAssetListState();
  const hasSCIAccess            = Features.hasSCIAccess();
  const dateValue               = useSelector(dateValueSelector);
  const queryDate               = useSelector(queryParamsDateSelector);
  const queryParams             = useSelector(queryParamsSelector);
  const ignoreQuery             = useSelector(ignoreQuerySelector);
  const isLiveTracking          = trackingMode === TrackingMode.Live;
  const [
    filterStartDate,
    setFilterStartDate
  ] = useState(trackingFilters?.date?.startDate || startOfDay());
  const [
    filterEndDate,
    setFilterEndDate
  ] = useState(trackingFilters?.date?.endDate || endOfDay());
  const [
    selectedDateFilterValue,
    setSelectedDateFilterValue
  ] = useState(trackingFilters?.date?.period || DateSelectorValues.Live);
  const {
    boundary,
    onClickBoundaryList,
    onApplyBoundary,
    toggleShowBoundaryPanel
  } = useBoundaryFilter({closeMobileMenu, moreFiltersRef});

  const moreFilterPropsRef    = useRef({});
  const skipSideSheetResetRef = useRef(false);

  const resetFleetUpdates = useFleetResetUpdates();

  const isTrackingBetaActive = useMemo(() =>
    isCurrentPage(route?.route, Route.TRACKING2) && trackingMode === TrackingMode?.Live
  , [route?.route, trackingMode]);

  const lastFilteredDateProps: LastFilteredDateProps = useMemo(() => ({
    startDate : queryDate ? dateToMoment(queryDate) : filterStartDate,
    endDate   : queryDate ? dateToMoment(queryDate) : filterEndDate,
    setFilterStartDate,
    setFilterEndDate,
    selectedDateFilterValue
  }), [filterStartDate, filterEndDate, selectedDateFilterValue, queryDate]);

  const boundaryFilterProps = useMemo(() => ({
    onClickBoundaryList,
    onApplyBoundary,
    selectFieldMinWidth : '0',
    disableClear        : !boundary?.length
  }), [
    boundary,
    onClickBoundaryList,
    onApplyBoundary
  ]);

  const createMoreFilters = useCallback(maxCount => {
    const showMore = filterList.length > maxCount;
    const more     = [];
    setShowMoreFilters(showMore);
    if (showMore) {
      filterList.forEach((filter, index) => {
        const show = index < maxCount;
        if (!show) {
          more.push({
            [filter] : filterMap[filter]?.({
              ...filterProps,
              ...groupFilterProps,
              ...moreFilterPropsRef.current.dateFilterProps,
              ...boundaryFilterProps
            })
          });
        }
      });
    }
    setMoreFilters(more);
  }, [filterList, isPhone, filterState, globalGroup, globalGroupId, boundaryFilterProps, accountHierarchy]);

  const isHistoryFilter = (filterType) => (
    filterType !== Filters.assetStatus &&
    filterType !== Filters.geoFences &&
    filterType !== Filters.routes
  );

  useEffect(() => {
    if (trackingMode === TrackingMode.History) {
      const historyFilters = filterList.filter(item => isHistoryFilter(item));
      setFilterList(historyFilters);
      resetFilterWrapper(historyFilters);
    } else {
      setFilterList(FiltersList);
      resetFilterWrapper(FiltersList);
    }
  }, [isLiveTracking]);

  useEffect(() => {
    if (filterList && !trackingSearchOpen) {
      resetFilterWrapper(filterList);
    }
  }, [filterList, trackingSearchOpen, lastFilteredDateProps]);

  useEffect(() => {
    if (!isObjectEmpty(filterParams)) {
      onSubmit?.();
      !isPhone && showMoreFilters && moreFiltersRef.current?.handleClose?.();
    }
  }, [filterParams]);

  useEffect(() => {
    if (!isPhone) {
      resetFilterWrapper(filterList);
    }
  }, [globalGroupId, dateFilterProps]);

  useEffect(() => {
    if (trackingFilters && !isEqual(trackingFilters, filterState)) {
      setFilterState(trackingFilters);
    }
  }, [trackingFilters]);

  useEffect(() => {
    !isPhone && dispatch(setIsMobileMapView(false));
  }, [isPhone]);

  const resetFilterWrapper = (filterList: Filters[]) => {
    const {width, count} = handleResize(filterList);
    setFilterWidth(width);
    setMaxFilterCount(count);
    createMoreFilters(count);
  };

  useEffect(() => {
    window.addEventListener('resize', debounce(() => resetFilterWrapper(filterList), 300));
    return () => window.removeEventListener('resize', () => resetFilterWrapper(filterList));
  }, [filterList]);

  useImperativeHandle(ref, () => ({
    clearAllSelections,
    onApply
  }));

  const toggleView = useCallback(function(viewId?: string) {
    setView({
      [Filters.groups]          : false,
      [Filters.assetTypes]      : false,
      [Filters.assetCategories] : false,
      [Filters.date]            : false,
      [Filters.geoFences]       : false,
      [Filters.routes]          : false,
      [Filters.assetStatus]     : false,
      [Filters.boundary]        : false,
      ...(viewId ? {[viewId]: true} : {})
    });
  }, []);

  const createFilterState = (selected, id = filterId) => {
    if (isFromGroupsAssetsFilter(id)) {
      // where filter is from groups and asset, update both fields
      const {
        selectedGroups,
        selectedAssets,
        selectedDevices
      } = selected || {};
      const state = getGroupsAssetsFilterUpdates({
        filterState,
        selectedGroups,
        selectedAssets,
        selectedDevices
      });
      return {
        ...filterState,
        ...state
      };
    }
    // default case for single filter
    return {
      ...filterState,
      [id] : {
        ...filterState[id],
        selected
      }
    };
  };

  const onApply = useCallback((selected, id = filterId, callback?: Function) => {
    if (id) {
      findNearbyIsActive && dispatch(resetTrackingAssetsState());
      const newState  = createFilterState(selected, id);
      const action = FilterActions[id];
      if (isFromGroupsAssetsFilter(id)) {
        selected = {
          selectedAssets  : newState.assets.selected,
          selectedGroups  : newState.groups.selected,
          selectedDevices : newState.devices.selected
        };
      }
      !skipSideSheetResetRef.current && resetSideSheet?.();
      skipSideSheetResetRef.current = false;
      if (action) {
        dispatch(action(selected, trackingMode, callback));
      } else {
        setFilterState(newState);
      }
      queryParams && dispatch(clearQueryParams());
      resetFleetUpdates();
    }
  }, [filterState, FilterActions, filterId, trackingMode, findNearbyIsActive]);

  const onSubmit = useFilterSubmit({
    filterId,
    filterParams,
    filterState,
    setFilterState,
    onApply
  });

  const onSubmitDateFilter = (filterParams) => {
    const mode = filterParams.selectedRadio === DateSelectorValues.Live ? TrackingMode.Live : TrackingMode.History;
    dispatch(setTrackingMode(mode));
    dispatch(applyAssetHistoryDateFilter(filterParams));
  };

  const handleSubmit = useCallback((filterId: Filters, filterParams: FilterParamsProps) => {
    setFilterId(filterId);
    setFilterParams(filterParams);
    toggleView();
    if (filterId === Filters.date) {
      onSubmitDateFilter(filterParams);
    } else {
      const {
        selections,
        selectedAssets,
        selectedDevices,
        selectedGroups
      } = filterParams;
      const isFilterSelected = !!(
        selections?.length ||
      selectedAssets?.length ||
      selectedDevices?.length ||
      selectedGroups?.length
      );
      dispatch(setSelectedFiltersActive(isFilterSelected));
    }
  }, []);

  const handleAction = useCallback(e => {
    !isPhone && showMoreFilters && moreFiltersRef?.current?.handleClose();
    e.stopPropagation();
    dispatch(setGeoFencePanelOpen(true));
  }, [isPhone, showMoreFilters]);

  const getSelectedCount = useCallback((filterId: string): string | null => {
    const selectedFiltersCount = filterId === 'groups'
      ? getUniqueFiltersCount(filterState[filterId]?.selected)
      : filterState[filterId]?.selected?.length;
    const selectedFilterCount = selectedFiltersCount;
    return selectedFilterCount ? `${selectedFilterCount}` : null;
  }, [filterState]);

  const clearAllSelections = useCallback((isResetTracking = false) => {
    let newState = {};
    Object.keys(filterState).forEach(key => {
      newState = {
        ...newState,
        [key] : {
          ...filterState[key],
          selected : []
        }
      };
    });
    resetSideSheet?.();
    resetFleetUpdates();
    setFilterState(newState, true);
    dispatch(applySaveFilter(null, newState, isResetTracking) as any);
    dispatch(setSelectedFiltersActive(false) as any);
    queryParams && dispatch(clearQueryParams());
    dispatch({type: RESET_FILTER_TREE_NODES, mode: null});
  }, [filterState]);

  const handleSearchChange = useCallback((
    e: ChangeEvent<{}>,
    value: string,
    reason: string
  ) => {
    if (value && reason !== 'reset') {
      dispatch(search(value, true));
    }
    if (reason === 'clear') {
      dispatch(clearSelectedTrackingSearchResult());
    }
  }, []);

  const onSearchBlur = useCallback(() => {
    setTrackingSearchOpen(false);
  }, []);

  const onSearchFocus = useCallback(() => {
    setTrackingSearchOpen(true);
    dispatch(setSideSheetStatus({
      desktopSideSheetOpen : false
    }));
  }, []);

  const onResultSelect = useCallback((
    e: ChangeEvent<{}>,
    value: (any)
  ) => {
    dispatch(setSelectedTrackingSearchResult(value, !showAssetsListRef.current) as any);
    if (value) setTrackingSearchOpen(false);
  }, []);

  const filterMap = {
    [Filters.groups]          : GroupsFilter,
    [Filters.assetTypes]      : TypesFilter,
    [Filters.assetCategories] : CategoriesFilter,
    [Filters.geoFences]       : GeofencesFilter,
    [Filters.routes]          : RoutesFilter,
    [Filters.assetStatus]     : StatusFilter,
    [Filters.date]            : DateFilter,
    [Filters.boundary]        : BoundaryFilter
  };

  const displayFilter = useCallback((type): boolean => {
    const filterIndex = filterList.indexOf(type);
    const show = (filterIndex > -1 && filterIndex < maxFilterCount);
    return ((!isPhone && show) || view[type]) &&
    !isArrayDeepEmpty(accountHierarchy);
  }, [filterList, maxFilterCount, isPhone, view, accountHierarchy]);

  // invokes for the every render to detect whether the
  // save filter must be disabled or not
  const isDisabledSaveFilter = useCallback(() => {
    return !Object.values(filterState)?.some(filter => filter.selected?.length > 0) &&
    Object.keys(savedPreferences)?.length <= 0;
  }, [filterState, savedPreferences]);

  // invokes for new filter save, remove and modification of exisiting save filter...
  const onFilterSave = useCallback(({state, filterKey, filter, isRemove = false}: FilterSave) => {
    if (state) {
      const currentFilter = filterKey && filter
        ? {
          ...state,
          [filterKey] : formatFilterState(filter, accountHierarchy)
        }
        : state;
      filterPersistence(TRACKING_FILTER_PREFERENCES, currentFilter, true);
      setSavedPreferences(currentFilter);
    }
    if (isRemove) {
      clearAllSelections();
    }
  }, []);

  // invokes when the any saved preference selected from save filter
  const onSavedFilterSelection = useCallback((name: string, state: FilterState) => {
    const formattedFilterState = formatFilterState(state, accountHierarchy);
    setFilterState(formattedFilterState, true);
    dispatch(applySaveFilter(name, formattedFilterState) as any);
    resetFleetUpdates();
  }, [globalGroupId, accountHierarchy]);

  // return boolean value that allow to show the menu...
  const showMenu = useCallback(() => isEmpty(Object.values(view).filter(Boolean)), [view]);

  const renderSaveFilter = () => {
    if (showMenu()) {
      const savefilterState = !isEmpty(filterState)
        ? {
          ...filterState,
          ...formatFilterState(filterState, accountHierarchy)
        }
        : filterState;
      return (
        <div id={!isPhone ? 'saveFilter' : ''} className={classnames(classes.filter,
          !isPhone ? classes.saveFilter : classes.mobileSave)}>
          <SaveFilter isMobileView={isPhone}
            appliedSaveFilter={appliedSaveFilter}
            filterState={savefilterState}
            savedPreferences={savedPreferences}
            onFilterSave={onFilterSave}
            disabled={isDisabledSaveFilter()}
            onSavedFilterSelection={onSavedFilterSelection} />
        </div>
      );
    };
    return null;
  };

  const updateTreeNodes = useCallback(function(nodes) {
    dispatch(setFilterTreeNodes({nodes}));
  }, []);

  const updateKeys = useCallback(function(keys: string[], type: string) {
    dispatch(updateTreeKeys({keys, type}));
  }, []);

  const onMultiDropdownChecked = useCallback((selectedGroup) => {
    const selected = {selectedGroups: selectedGroup, selectedAssets: filterState?.assets?.selected};
    const newState  = createFilterState(selected, 'groups');
    setFilterState(newState);
  }, [setFilterState]);

  const sciDeviceSearchEnabled = useCallback((isTrackingBetaActive: boolean, defaultAccess: boolean) =>
    !isTrackingBetaActive && defaultAccess, []);

  const scFilterProps: SCFilterProps = useMemo(() => ({
    SCDeviceTypes,
    onCheckIncludeSCDevices,
    hasSCIAccess    : sciDeviceSearchEnabled(isTrackingBetaActive, hasSCIAccess),
    includeSCDevice : sciDeviceSearchEnabled(isTrackingBetaActive, includeSCDevice)
  }), [hasSCIAccess, includeSCDevice, SCDeviceTypes, onCheckIncludeSCDevices, isTrackingBetaActive]);

  useEffect(() => {
    if (isLiveTracking) {
      setSelectedDateFilterValue(DateSelectorValues.Live);
      setFilterStartDate(startOfDay());
      setFilterEndDate(endOfDay());
    }
  }, [isLiveTracking]);

  useEffect(() => {
    setFilterStartDate(filterState?.date?.startDate);
    setFilterEndDate(filterState?.date?.endDate);
  }, [filterState?.date?.endDate, filterState?.date?.startDate]);

  useEffect(() => {
    setSelectedDateFilterValue(dateValue);
  }, [dateValue]);

  const groupTreeRef = useRef(null);

  useEffect(() => {
    const searchedItem = searchResult?.item;
    if (searchedItem) {
      const state         = {...((filterState as any) ?? {})};
      let selectedAssets  = [...state.assets.selected];
      let selectedDevices = [...state.devices.selected];
      const filterAsset   = {
        accountId : searchedItem?.accountId,
        id        : searchedItem?.id?.toString(),
        name      : searchedItem?.assetName || searchedItem?.deviceName
      };
      if (searchResult.isAsset || searchResult.isFcDevice || searchResult.isDriver) {
        selectedAssets = uniqBy([...(selectedAssets ?? []), filterAsset], 'id');
      } else if (searchResult.isScDevice || isSCIDevice(searchResult)) {
        selectedDevices = uniqBy([...(selectedDevices ?? []), filterAsset], 'id');
      }
      if (!searchResult.isGeofence && !searchResult.isPlace) {
        const callback = () => {
          const timeout = setTimeout(() => {
            groupTreeRef.current?.addItem?.(filterAsset);
            clearTimeout(timeout);
          }, 750);
        };
        skipSideSheetResetRef.current = true;
        onApply({
          selectedGroups : state.groups.selected,
          selectedAssets,
          selectedDevices
        }, Filters.groups, callback);
      }
    }
  }, [searchResult]);

  const filterProps: FilterParamsType = useMemo(() => ({
    isPhone,
    toggleView,
    filterState,
    handleSubmit,
    globalGroup,
    globalGroupId,
    handleAction,
    accountHierarchy,
    ancestorAccountIds,
    descendantAccountId
  }), [
    isPhone,
    toggleView,
    filterState,
    handleSubmit,
    globalGroup,
    globalGroupId,
    handleAction,
    accountHierarchy,
    ancestorAccountIds,
    descendantAccountId
  ]);

  const groupFilterProps: GroupsFilterParamsType = useMemo(() => ({
    assetLoading,
    appliedSaveFilter,
    scFilterProps,
    nodeKeys,
    treeNodes,
    treeKeys,
    currentGroup,
    cachedData,
    updateTreeNodes,
    onUpdateItemStored,
    updateKeys,
    groupTreeRef,
    onSelected : onLoadGroupAssets
  }), [
    assetLoading,
    appliedSaveFilter,
    scFilterProps,
    nodeKeys,
    treeNodes,
    treeKeys,
    currentGroup,
    cachedData,
    updateTreeNodes,
    onUpdateItemStored,
    updateKeys,
    onLoadGroupAssets
  ]);

  const dateFilterProps: DateFilterParamsType = useMemo(() => {
    const props = {
      setSelectedDateFilterValue,
      selectedDateFilterValue,
      queryDate,
      ignoreQuery,
      lastFilteredDateProps,
      includeLive           : true,
      isDefaultValueOnMount : true,
      isBackToMenu          : isLiveTracking,
      disabled              : !hasHistoryAccess()
    };
    moreFilterPropsRef.current = {
      ...moreFilterPropsRef,
      dateFilterProps : props
    };
    return props;
  }, [
    setSelectedDateFilterValue,
    selectedDateFilterValue,
    queryDate,
    ignoreQuery,
    lastFilteredDateProps,
    trackingMode
  ]);

  const shouldDisplayClearAll = hasSelectionInFilters(filterState) || !isLiveTracking;

  const toggleMobileBoundaryFilter = function() {
    if (isLiveTracking) {
      toggleShowBoundaryPanel?.(true);
      dispatch(setGeoFencePanelOpen(true));
      closeMobileMenu();
    } else {
      toggleView(Filters.boundary);
    }
  };

  const clearAllMobile = function() {
    clearAllSelections();
    returnToLiveTracking();
    onApplyBoundary();
  };

  return (
    <div className={isPhone ? classes.mobileTrackingFiltersWrapper : classes.desktopTrackingFiltersWrapper}>
      <Hidden show='mobile'>
        {showMenu() && (
          <>
            <div className={classes.header}>
              <div>
                <h4 className={classes.title}>
                  {translate('common:filters.allFilters')}
                </h4>
              </div>
              {shouldDisplayClearAll && (
                <div className={classes.mobileClear}
                  onClick={clearAllMobile}>
                  {translate('nav:filters.expanded.clearAll')}
                </div>
              )}
            </div>
            <MobileMenuButton includeChevron
              label={translate('common:filters.groupsAssets')}
              selectFieldValue={getSelectedCounts(filterState?.groups, filterState?.assets, filterState?.devices)}
              onClick={function() {
                toggleView(Filters.groups);
              }} />
            <MobileMenuButton includeChevron
              label={translate('common:filters.assetType')}
              selectFieldValue={getSelectedCount(Filters.assetTypes)}
              onClick={function() {
                toggleView(Filters.assetTypes);
              }} />
            <MobileMenuButton includeChevron
              label={translate('common:filters.assetCategory')}
              selectFieldValue={getSelectedCount(Filters.assetCategories)}
              onClick={function() {
                toggleView(Filters.assetCategories);
              }} />
            <MobileMenuButton includeChevron
              label={translate('common:time.dates')}
              selectFieldValue={dateFilterMapping(selectedDateFilterValue)}
              onClick={function() {
                toggleView(Filters.date);
              }} />
            {isLiveTracking && (
              <>
                <MobileMenuButton includeChevron
                  label={translate('common:filters.geofences')}
                  selectFieldValue={getSelectedCount(Filters.geoFences)}
                  onClick={function() {
                    toggleView(Filters.geoFences);
                  }} />
                <MobileMenuButton includeChevron
                  label={translate('common:filters.route')}
                  selectFieldValue={getSelectedCount(Filters.routes)}
                  onClick={function() {
                    toggleView(Filters.routes);
                  }} />
                <MobileMenuButton includeChevron
                  label={translate('common:filters.status')}
                  selectFieldValue={getSelectedCount(Filters.assetStatus)}
                  onClick={function() {
                    toggleView(Filters.assetStatus);
                  }} />
              </>
            )}
            <div className={classes.header}>
              <h4 className={classes.title}>{translate('nav:filters.savedFilters')}</h4>
            </div>
            {renderSaveFilter()}
            {showMenu() && (
              <div className={classes.closeFilters}>
                <PrimaryButton fullWidth
                  onClick={closeMobileMenu ? function() {
                    closeMobileMenu();
                  } : null}>
                  <span>{translate('nav:filters.collapsed.closeFilters')}</span>
                </PrimaryButton>
              </div>
            )}
          </>
        )}
      </Hidden>
      {!isPhone && (
        <div id='trackingSearchWrapper'
          className={classnames(
            classes.autoSearchWrapper,
            trackingSearchOpen ? classes.fullWidthSearch : null
          )}>
          <AutocompleteSearch fullWidthOnOpen
            disablePopperPortal
            options={trackingSearchOptions}
            onChange={handleSearchChange}
            searchResult={searchResult}
            onFocus={onSearchFocus}
            onBlur={onSearchBlur}
            onResultSelect={onResultSelect}
            loading={trackingSearchLoading}
            portalClassOverride={classes.autocompletePopper}
            recentSearches={trackingSearchHistory}
            shortPlaceholder={!trackingSearchOpen}
            arrowKeyNavigationEnabled={true}
            isClearSearch />
        </div>
      )}
      {!trackingSearchOpen && (
        <div id='filterWrapper' className={!isPhone ? classnames(
          classes.filterWrapper,
          trackingSearchOpen ? classes.hideFilters : null
        ) : null}>
          {shouldRenderGroupsFilter(
            isPhone,
            accountHierarchy,
            displayFilter(Filters.groups)
          ) && (
            <div className={classnames('filter', classes.filter)} style={{display: displayFilter(Filters.groups) ? 'block' : 'none'}}>
              {filterMap[Filters.groups]?.({
                ...filterProps,
                ...groupFilterProps
              })}
            </div>
          )}
          {displayFilter(Filters.assetTypes) && (
            <div className={classnames('filter', classes.filter)}>
              {filterMap[Filters.assetTypes]?.({
                ...filterProps
              })}
            </div>
          )}
          {displayFilter(Filters.assetCategories) && (
            <div className={classnames('filter', classes.filter)}>
              {filterMap[Filters.assetCategories]?.({
                ...filterProps
              })}
            </div>
          )}
          {displayFilter(Filters.date) && (
            <div className={classes.filter}>
              {filterMap[Filters.date]?.({
                ...filterProps,
                ...dateFilterProps
              })}
            </div>
          )}
          {displayFilter(Filters.geoFences) && (
            <div className={classnames('filter', classes.filter)}>
              {filterMap[Filters.geoFences]?.({
                ...filterProps,
                filterRefresh : !savingGeofence
              })}
            </div>
          )}
          {displayFilter(Filters.routes) && (
            <div className={classnames('filter', classes.filter)}>
              {filterMap[Filters.routes]?.({
                ...filterProps
              })}
            </div>
          )}
          {displayFilter(Filters.assetStatus) && (
            <div className={classnames('filter', classes.filter)}>
              {filterMap[Filters.assetStatus]?.({
                ...filterProps
              })}
            </div>
          )}
          {displayFilter(Filters.boundary) && (
            <div className={classnames('filter', classes.filter)}>
              {filterMap[Filters.boundary]?.({
                ...filterProps,
                ...boundaryFilterProps
              })}
            </div>
          )}
          {!isPhone && showMoreFilters && !isArrayDeepEmpty(accountHierarchy) && (
            <MoreFilters show={showMoreFilters}
              commonProps={{
                selectedFilterValue : filterState?.[Filters.date]?.period,
                disableClear        : !boundary?.length
              }}
              width={filterWidth}
              ref={moreFiltersRef}
              options={moreOptionValues}
              count={moreOptionsCount}
              accountId={globalGroupId}
              accountIds={getSubAccount(accountHierarchy[0], [globalGroupId])}
              filterState={filterState}
              ancestorAccountIds={ancestorAccountIds}
              descendantAccountId={descendantAccountId} />
          )}
          {!isPhone && renderSaveFilter()}
        </div>
      )}
    </div>
  );
});

export default TrackingFilters;
